import { Client, ICountryConfig, IUser, Servicepoint, Subclient, User } from "@/entities";
import { Ability, AbilityBuilder, AbilityClass, InferSubjects } from "@casl/ability";
import { RawRuleOf } from "@casl/ability/dist/types/RuleIndex";

type Actions = "manage" | "create" | "read" | "update" | "delete";
type Subjects =
  | InferSubjects<typeof User | typeof Client | typeof Subclient | typeof Servicepoint>
  | "FirmsPage"
  | "OrdersPage"
  | "DepositPage"
  | "Pickup"
  | "Delivery"
  | "DeliveryExchange"
  | "AssistanceService"
  | "SupplyMaterialService"
  | "AdministrationPage"
  | "ReportsPage"
  | "UsersPage"
  | "UserEditPage"
  | "UserEditAdministration"
  | "UserEditSupervision"
  | "UserEditApprover"
  | "ClientsPage"
  | "ClientAddPage"
  | "ClientEditPage"
  | "ClientEditActive"
  | "ReportTypeSearchEmployee"
  | "ReportTypeOrders"
  | "ReportTypeUsers"
  | "ReportTypeClient"
  | "ReportTypeSubclient"
  | "ReportTypeServicepoint"
  | "SubclientsPage"
  | "SubclientAddPage"
  | "SubclientEditPage"
  | "SubclientEditActive"
  | "ServicepointsPage"
  | "ServicepointAddPage"
  | "ServicepointEditPage"
  | "ServicepointEditActive"
  | "UserTypes"
  | "Countries"
  | "Status"
  | "AllReports"
  | "all"
  | "CountriesPage";

export type AppAbilityType = Ability<[Actions, Subjects]>;
export const AppAbility = Ability as AbilityClass<AppAbilityType>;

function loadAbilityAdministration(user: User | undefined, can: any): void {
  if (user?.roles?.find((role) => role.code === "ROL_CASH_MYPROSEGURAPP_ADMINISTRATION")) {
    can("read", "AdministrationPage");
    can("read", "ClientsPage");
    can("read", "SubclientsPage");
    can("read", "ServicepointsPage");
    can("manage", Client);
    can("manage", Subclient);
    can("manage", Servicepoint);

    if (user?.userType === "PROSEGUR") {
      can("read", "ClientAddPage");
      can("read", "ClientEditPage");
      can("read", "ClientEditActive");
      can("read", "SubclientAddPage");
      can("read", "SubclientEditPage");
      can("read", "SubclientEditActive");
      can("read", "ServicepointAddPage");
      can("read", "ServicepointEditPage");
      can("read", "ServicepointEditActive");
      can("read", "CountriesPage");
    }
  }
}

function loadAbilityUserManagement(user: User | undefined, can: any): void {
  if (user?.roles?.find((role) => role.code === "ROL_CASH_MYPROSEGURAPP_USER-MANAGEMENT")) {
    can("read", "AdministrationPage");
    can("read", "UsersPage");
    can("read", "UserEditPage");
    can("manage", User);
  }

  if (user?.userType === "PROSEGUR") {
    can("read", "UserTypes");
    can("read", "Status");
    if (user.admin) {
      can("read", "UserEditAdministration");
      can("read", "UserEditSupervision");
      can("read", "UserEditApprover");
      can("read", "AllReports");
      can("read", "Countries");
    }
  }

  if (user?.supervisor) {
    can("read", "UserEditSupervision");
  }
  if (user?.approver) {
    can("read", "UserEditApprover");
  }
}

function loadAbilityReportType(user: User | undefined, can: any, config?: ICountryConfig): void {
  if (!config?.Reports.Enabled) {
    return;
  }

  const reportType = config?.Reports?.ReportType;

  if (reportType?.SearchEmployee?.Enabled) {
    can("read", "ReportTypeSearchEmployee");
  }

  if (reportType?.Orders?.Enabled) {
    can("read", "ReportTypeOrders");
  }

  if (reportType?.Users?.Enabled) {
    can("read", "ReportTypeUsers");
  }

  if (reportType?.Client?.Enabled) {
    can("read", "ReportTypeClient");
  }

  if (reportType?.Subclient?.Enabled) {
    can("read", "ReportTypeSubclient");
  }

  if (reportType?.Servicepoint?.Enabled) {
    can("read", "ReportTypeServicepoint");
  }
}

function loadAbilitySearchEmployee(
  user: User | undefined,
  can: any,
  config?: ICountryConfig
): void {
  if (
    config?.SearchEmployee.Enabled &&
    user?.roles?.find((role) => role.code === "ROL_CASH_MYPROSEGURAPP_EMPLOYEE")
  ) {
    can("read", "FirmsPage");
  }
}

function loadAbilityOrders(user: User | undefined, can: any, config?: ICountryConfig): void {
  if (config?.Orders.Enabled) {
    if (user?.roles?.find((role) => role.code === "ROL_CASH_MYPROSEGURAPP_PICKUP")) {
      can("read", "OrdersPage");
      can("manage", "OrdersPage");
      can("manage", "Pickup");
    }

    if (user?.roles?.find((role) => role.code === "ROL_CASH_MYPROSEGURAPP_DELIVERY")) {
      can("read", "OrdersPage");
      can("manage", "OrdersPage");
      can("manage", "Delivery");
    }

    if (user?.roles?.find((role) => role.code === "ROL_CASH_MYPROSEGURAPP_DELIVERY-EXCHANGE")) {
      can("read", "OrdersPage");
      can("manage", "OrdersPage");
      can("manage", "DeliveryExchange");
    }

    if (user?.roles?.find((role) => role.code === "ROL_CASH_MYPROSEGURAPP_ASSISTANCE-SERVICE")) {
      can("read", "OrdersPage");
      can("manage", "OrdersPage");
      can("manage", "AssistanceService");
    }

    if (
      user?.roles?.find((role) => role.code === "ROL_CASH_MYPROSEGURAPP_SUPPLY-MATERIAL-SERVICE")
    ) {
      can("read", "OrdersPage");
      can("manage", "OrdersPage");
      can("manage", "SupplyMaterialService");
    }

    if (user?.roles?.find((role) => role.code === "ROL_CASH_MYPROSEGURAPP_ORDERS")) {
      can("read", "OrdersPage");
    }
  }
}

function loadAbilityReportsPage(
  rules: RawRuleOf<Ability<[Actions, Subjects]>>[],
  can: any,
  config?: ICountryConfig
): void {
  if (config?.Reports.Enabled) {
    const canReadOrdersPage = rules.find(
      (rule) => rule.subject === "OrdersPage" && rule.action === "read"
    );

    const canReadFirmsPage = rules.find(
      (rule) => rule.subject === "FirmsPage" && rule.action === "read"
    );

    const canReadAdministrationPage = rules.find(
      (rule) => rule.subject === "AdministrationPage" && rule.action === "read"
    );

    if (canReadOrdersPage || canReadFirmsPage || canReadAdministrationPage) {
      can("read", "ReportsPage");
    }
  }
}

function loadAbilityDeposit(user: User | undefined, can: any): void {
  if (user?.roles?.find((role) => role.code === "ROL_CASH_MYPROSEGURAPP_DEPOSIT")) {
    can("read", "DepositPage");
  }
}

export function defineRulesFor(
  user?: User,
  config?: ICountryConfig
): RawRuleOf<Ability<[Actions, Subjects]>>[] {
  const { can, rules } = new AbilityBuilder(AppAbility);

  loadAbilitySearchEmployee(user, can, config);
  loadAbilityOrders(user, can, config);
  loadAbilityUserManagement(user, can);
  loadAbilityAdministration(user, can);
  loadAbilityDeposit(user, can);
  loadAbilityReportType(user, can, config);
  loadAbilityReportsPage(rules, can, config);

  return rules;
}

export function buildAbilityFor(user: User, config?: ICountryConfig): AppAbilityType {
  return new AppAbility(defineRulesFor(user, config));
}

export const mockAbility = (user?: IUser, config?: ICountryConfig): AppAbilityType =>
  new AppAbility(defineRulesFor(user, config));
